/**
 * The DS Tooltip's text checks (STFFLG-4236): whether its content only repeats the child's text,
 * and whether that text is cut off as rendered. A repeat adds no description, and shows its bubble
 * only while the text is cut off.
 */
import React, {type ReactNode} from 'react'

/** Overflow below this many CSS px is float noise; Chrome's layout unit is 1/64 px. */
const EPSILON = 0.01

const clips = (overflow: string) => overflow === 'hidden' || overflow === 'clip'
const scrolls = (overflow: string) => overflow === 'auto' || overflow === 'scroll'

const px = (value: string) => parseFloat(value) || 0

/**
 * Whether the text a tooltip trigger shows is cut off, as rendered right now.
 *
 * The DS Tooltip reads this the moment a truncation tooltip would show (hover or focus), so the
 * answer follows the window and the column as they are: narrow the window until a cell ends in
 * "…" and its tooltip starts showing; widen it and the tooltip stops. Text exactly as wide as its
 * box is not cut off.
 *
 * Text is cut off when a box that clips it (`overflow: hidden` / `clip`) is narrower than the
 * text: any such box between the text and the trigger (an `EllipsisText`, a `TableCell ellipsis`
 * cell, a StatusPill's label), and the nearest one around the trigger (a table cell holding a
 * plain `<Tooltip content={v}>{v}</Tooltip>`). A box that scrolls (`auto` / `scroll`) cuts
 * nothing, as scrolling brings the text into view, and nor does a box that lets text spill out
 * visibly. A `-webkit-line-clamp` box cuts the lines past its clamp.
 *
 * Read two ways, either of which counts:
 * - each text's own rect against the content edge of every box that clips it, to the sub-pixel:
 *   the browser draws "…" for an overflow well under a pixel, which `scrollWidth`, an integer,
 *   rounds away;
 * - `scrollWidth > clientWidth` on each clipping box in the trigger, which needs no text rects
 *   (a test environment has none).
 *
 * Out-of-flow content (`position: absolute` / `fixed`: an in-place bubble, `VisuallyHidden`
 * text, a corner badge) is not the line the eye reads, so it is left out.
 */
export const isTextTruncated = (trigger: HTMLElement): boolean => {
  const styles = new Map<Element, CSSStyleDeclaration>()
  const styleOf = (element: Element) => {
    let style = styles.get(element)
    if (!style) {
      style = getComputedStyle(element)
      styles.set(element, style)
    }
    return style
  }
  // `overflow-x`, or the shorthand where a style engine only keeps that (jsdom).
  const overflowOf = (element: Element) => styleOf(element).overflowX || styleOf(element).overflow
  const outOfFlow = (element: Element) => ['absolute', 'fixed'].includes(styleOf(element).position)
  const contentEnd = (element: Element) => {
    const style = styleOf(element)
    return (
      element.getBoundingClientRect().right - px(style.borderRightWidth) - px(style.paddingRight)
    )
  }

  // The nearest box around the trigger that clips it, if the nearest that handles overflow at all
  // clips rather than scrolls.
  let outerClip: Element | null = null
  for (let box = trigger.parentElement; box; box = box.parentElement) {
    const overflow = overflowOf(box)
    if (clips(overflow)) outerClip = box
    if (clips(overflow) || scrolls(overflow)) break
  }

  // The boxes in the trigger that clip `node`, from its parent up; null when it is out of flow.
  const clipsInTrigger = (node: Node): Element[] | null => {
    const boxes: Element[] = []
    for (let box = node.parentElement; box; box = box.parentElement) {
      if (outOfFlow(box)) return null
      if (clips(overflowOf(box))) boxes.push(box)
      if (box === trigger) return boxes
    }
    return boxes
  }

  const range = document.createRange()
  const canMeasureText = typeof range.getBoundingClientRect === 'function'
  const walker = document.createTreeWalker(trigger, NodeFilter.SHOW_TEXT)
  for (let text = walker.nextNode(); text; text = walker.nextNode()) {
    if (!text.textContent?.trim()) continue
    const inner = clipsInTrigger(text)
    if (!inner) continue
    const boxes = outerClip ? [...inner, outerClip] : inner
    if (canMeasureText && boxes.length > 0) {
      range.selectNodeContents(text)
      const rect = range.getBoundingClientRect()
      if (rect.width > 0 && boxes.some((box) => rect.right > contentEnd(box) + EPSILON)) {
        return true
      }
    }
    const cut = inner.some((box) => {
      if (box.scrollWidth > box.clientWidth) return true
      const clamp = styleOf(box).getPropertyValue('-webkit-line-clamp')
      return clamp !== '' && clamp !== 'none' && box.scrollHeight > box.clientHeight
    })
    if (cut) return true
  }
  return false
}

/** The text a node renders through its children: strings and numbers, down through elements. */
export const textOfChildren = (node: ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(textOfChildren).join('')
  if (React.isValidElement<{children?: ReactNode}>(node)) return textOfChildren(node.props.children)
  return ''
}

/** True when `content` only repeats the child's own text, so describing the child would echo it. */
export const repeatsChildText = (content: ReactNode, children: ReactNode) =>
  (typeof content === 'string' || typeof content === 'number') &&
  String(content).trim() !== '' &&
  textOfChildren(children).trim() === String(content).trim()

/** The text the trigger renders, leaving out an in-place bubble inside it. */
export const renderedTextOf = (trigger: HTMLElement, bubble: HTMLElement | null) =>
  Array.from(trigger.childNodes, (node) => (node === bubble ? '' : node.textContent ?? '')).join('')
